import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { GOAL_STATUS, RATING_LABEL, todayPerth } from '@pti/domain';
import { api } from '../api';
import { useCan } from '../hooks';
import { fmtDate } from '../format';
import { ErrorNote, Loading, Pill, Section } from '../components/ui';
import { BandMeter, RatingChip } from '../components/Ratings';
import { ConcernDialog } from './Performance';

interface PersonPerf {
  reviews: { id: number; periodYear: number; cycleStatus: string; proposed: number | null; final: number | null; why: string | null; calibrationReason: string | null; comment: string | null; status: string; documentId: number | null }[];
  plans: { year: number; plan: { status: string; documentId: number | null; goals: { goal: string; measure: string | null; dueDate: string | null; status: string; statusReason: string | null }[]; fundedTotal: number } | null; checkIn: { bookedFor: string | null; heldOn: string | null; needs: string | null; support: string | null; concern: string | null } | null }[];
  concerns: { id: number; periodYear: number; raisedOn: string | null; source: string; text: string; recordedBy: string; resolvedOn: string | null }[];
}

export function PersonPerformance({ personId, name }: { personId: string; name: string }) {
  const can = useCan();
  const qc = useQueryClient();
  const [concern, setConcern] = useState(false);
  const q = useQuery({ queryKey: ['person-perf', personId], queryFn: () => api.get<PersonPerf>(`/api/performance/people/${personId}`) });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const d = q.data!;
  const tone: Record<string, string> = { on: 'ok', risk: 'warn', off: 'bad', done: 'ok', superseded: 'muted' };
  return (
    <div className="grid-2">
      <div className="stack">
        <Section title="Annual reviews" aside={<Link className="link small" to="/performance">Open Performance</Link>}>
          {d.reviews.length ? d.reviews.map((r) => (
            <div key={r.id} className="goal">
              <span className="row between"><strong>{r.periodYear} annual review</strong><span className="row gap-s">{r.final && r.final !== r.proposed && <RatingChip r={r.proposed} muted />}<RatingChip r={r.final ?? r.proposed} label /></span></span>
              {r.why && <span className="small">{r.why}</span>}
              {r.calibrationReason && <span className="small dim">Calibration: {r.calibrationReason}</span>}
              {r.comment && <span className="small dim">Employee comment: “{r.comment}”</span>}
              {r.documentId && <Link className="link small" to={`/documents/${r.documentId}`}>Review record (Form 2)</Link>}
              {!r.final && <span className="small dim">Proposed — not final ({RATING_LABEL[r.proposed ?? 0] ?? 'not rated yet'}).</span>}
            </div>
          )) : <p className="small dim">No annual reviews recorded yet.</p>}
        </Section>
        <Section title="Recorded concerns" aside={can('performance:write') ? <button className="link small" onClick={() => setConcern(true)}>+ Record a concern</button> : null}>
          {d.concerns.length ? <ul className="plain-list small">{d.concerns.map((c) => <li key={c.id}><strong>{fmtDate(c.raisedOn)} · {c.periodYear} period</strong><span>{c.text}</span><span className="dim">{c.source === 'checkin' ? 'Raised at the mid-year check-in' : 'Recorded'} by {c.recordedBy}</span></li>)}</ul> : <p className="small dim">None.</p>}
        </Section>
      </div>
      <div className="stack">
        {d.plans.length ? d.plans.map((p) => (
          <Section key={p.year} title={`${p.year} plan & check-in`}>
            {p.plan?.goals.map((g, i) => <div key={i} className="goal"><span className="row between"><strong>{g.goal}</strong><Pill tone={tone[g.status]}>{GOAL_STATUS[g.status]}</Pill></span><span className="small dim">Measure: {g.measure ?? '—'} · by {fmtDate(g.dueDate)}</span></div>)}
            {p.plan?.documentId && <Link className="link small" to={`/documents/${p.plan.documentId}`}>Plan (Form 3)</Link>}
            <p className="small">{p.checkIn?.heldOn ? `Check-in held ${fmtDate(p.checkIn.heldOn)}.` : p.checkIn?.bookedFor ? `Check-in booked ${fmtDate(p.checkIn.bookedFor)}${p.checkIn.bookedFor > `${p.year}-09-30` ? ' — after the window.' : '.'}` : 'No check-in recorded.'}</p>
            {p.checkIn?.concern && <div className="callout warn small"><strong>Concern recorded:</strong> {p.checkIn.concern}</div>}
          </Section>
        )) : <Section title="Plans & check-ins"><p className="small dim">No plan recorded. Create one in Performance → Plans & check-ins.</p></Section>}
      </div>
      {concern && <ConcernDialog personId={personId} name={name} year={Number(todayPerth().slice(0, 4))} onClose={() => setConcern(false)} onDone={() => void qc.invalidateQueries({ queryKey: ['person-perf', personId] })} />}
    </div>
  );
}

interface PayData {
  payRate: number | null; payBasis: string | null; fteEq: number | null; band: { name: string; min: number; max: number; year: number; pdRef: string | null; awardRef: string | null } | null;
  position: number | null; positionLabel: string | null; history: { rate: number; basis: string; effectiveFrom: string | null; source: string; note: string | null; recordedBy: string }[];
}
const aud = (n: number, cents = false) => n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 });

export function PersonPay({ personId }: { personId: string }) {
  const q = useQuery({ queryKey: ['person-pay', personId], queryFn: () => api.get<PayData>(`/api/people/${personId}/pay`) });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const d = q.data!;
  return (
    <div className="grid-2">
      <Section title="Current pay" aside={<Link className="link small" to="/remuneration">Open Remuneration</Link>}>
        <dl className="kv">
          <div><dt>Rate</dt><dd>{d.payRate != null ? `${aud(d.payRate, d.payBasis === 'hour')} ${d.payBasis === 'hour' ? 'per hour (base, ex loading)' : 'per annum'}` : '—'}</dd></div>
          <div><dt>Full-time equivalent</dt><dd>{d.fteEq != null ? aud(d.fteEq) : '—'}</dd></div>
          <div><dt>Band {d.band?.year}</dt><dd>{d.band ? `${d.band.name}: ${aud(d.band.min)} – ${aud(d.band.max)}` : 'No band for this role'}</dd></div>
          {d.position != null && <div><dt>Position in band</dt><dd><BandMeter pos={d.position} /><span className={`small ${d.position < 0 ? 'tone-bad' : ''}`}>{d.positionLabel} · {Math.round(d.position * 100)}%</span></dd></div>}
          {d.band?.pdRef && <div><dt>Position description</dt><dd>{d.band.pdRef}</dd></div>}
          {d.band?.awardRef && <div><dt>Award</dt><dd>{d.band.awardRef}</dd></div>}
        </dl>
      </Section>
      <Section title="Pay history">
        {d.history.length ? (
          <table className="tbl mini"><thead><tr><th>From</th><th className="r">Rate</th><th>Why</th></tr></thead><tbody>
            {d.history.map((h, i) => <tr key={i}><td>{fmtDate(h.effectiveFrom)}</td><td className="r num">{aud(h.rate, h.basis === 'hour')}{h.basis === 'hour' ? '/hr' : ''}</td><td className="small">{h.note ?? h.source}</td></tr>)}
          </tbody></table>
        ) : <p className="small dim">No changes recorded yet. Every change from now on is kept (Fair Work: 7 years).</p>}
      </Section>
    </div>
  );
}
